import { useState, useMemo, useEffect, useCallback } from 'react';
import { format, startOfDay } from 'date-fns';
import { publicBookingApi } from '../../api/publicBooking';
import { ApiError } from '../../api/client';
import {
	ServiceMultiSelect,
	AddOnPicker,
	BookingDateTime,
	BookingProgress,
	BookingSummaryRow,
	StylistPicker,
	BookingPayment,
	retrievePaymentStatus,
	type StylistChoice,
	type BookingAmounts,
} from '../../components/booking';
import {
	saveConfirmationSnapshot,
	readConfirmationSnapshot,
	clearConfirmationSnapshot,
	getReturnedClientSecret,
	stripPaymentParamsFromUrl,
	type ConfirmationRow,
} from '../../utils/bookingConfirmation';
import { usePublicBookingSelection } from '../../hooks/usePublicBookingSelection';
import {
	formatBookingCurrency,
	buildAppointmentDateTime,
	buildWaitlistDateTime,
	calcFees,
} from '../../utils/bookingFormat';
import { formatSize, formatLength } from '../../constants/serviceOptions';
import type {
	PublicBookingInfo,
	PublicBookingSlot,
	CreatePublicBookingResponse,
	WaitlistPreference,
} from '../../types/publicBooking';

const STEPS = ['Stylist', 'Client Info', 'Service', 'Date & Time', 'Review', 'Payment'];

const STEP_STYLIST = 1;
const STEP_CLIENT = 2;
const STEP_SERVICE = 3;
const STEP_DATETIME = 4;
const STEP_REVIEW = 5;
const STEP_PAYMENT = 6;

/** Add-ons are a sub-step of Service, not a seventh number on the rail. */
type ServiceSubStep = 'service' | 'addons';

const WAITLIST_LABELS: Record<WaitlistPreference, string> = {
	ANY_TIME: 'Any time',
	MORNING: 'Morning',
	AFTERNOON: 'Afternoon',
};

interface BookWithPaymentProps {
	token: string;
	info: PublicBookingInfo;
}

interface ClientForm {
	firstName: string;
	lastName: string;
	phone: string;
	email: string;
}

const EMPTY_CLIENT: ClientForm = { firstName: '', lastName: '', phone: '', email: '' };

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Book with payment — the deposit-taking booking link.
 *
 * Six steps then a confirmation. The appointment is created when the customer
 * leaves the review step: `create` returns the PaymentIntent secret that step 6
 * needs, so the booking necessarily exists before the deposit is charged.
 *
 * A fully booked day opens a second path: a waiting-list request, which takes
 * no payment and therefore skips step 6 entirely.
 */
const BookWithPayment = ({ token, info }: BookWithPaymentProps) => {
	const [currentStep, setCurrentStep] = useState(STEP_STYLIST);
	const [serviceSubStep, setServiceSubStep] = useState<ServiceSubStep>('service');
	const [stylist, setStylist] = useState<StylistChoice>(undefined);
	const [client, setClient] = useState<ClientForm>(EMPTY_CLIENT);
	const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof ClientForm, string>>>({});
	const [stepError, setStepError] = useState<string | null>(null);
	const [notes, setNotes] = useState('');
	// Defaults to today so the Date & Time step opens with that day's
	// availability already loading, instead of an empty panel that only
	// starts fetching once the customer clicks a date. A time slot is still
	// required to continue, so nothing is booked for today by default.
	const [selectedDate, setSelectedDate] = useState<Date | null>(() => startOfDay(new Date()));
	const [selectedSlot, setSelectedSlot] = useState<PublicBookingSlot | null>(null);
	const [waitlist, setWaitlist] = useState<WaitlistPreference | null>(null);
	const [isCreating, setIsCreating] = useState(false);
	const [booking, setBooking] = useState<CreatePublicBookingResponse | null>(null);
	/** Booking inputs the current `booking` was created from — see handleProceedFromReview. */
	const [bookingSignature, setBookingSignature] = useState<string | null>(null);
	const [isPaid, setIsPaid] = useState(false);
	const [isWaitlisted, setIsWaitlisted] = useState(false);
	/** Confirmation rebuilt from storage after an off-site payment redirect. */
	const [restoredRows, setRestoredRows] = useState<ConfirmationRow[] | null>(null);
	const [restoredReference, setRestoredReference] = useState<string | null>(null);
	const [isCheckingReturn, setIsCheckingReturn] = useState(
		() => getReturnedClientSecret(window.location.search) !== null
	);

	const addOns = useMemo(() => info.addOns ?? [], [info.addOns]);
	const selection = usePublicBookingSelection(info.services, addOns);
	const staffList = info.staff ?? [];
	const isConfirmed = isPaid || isWaitlisted;
	const joiningWaitlist = waitlist !== null;

	// The API's deposit wins once the booking exists; before that the schedule
	// the booking link published previews it.
	const serviceTotal = booking?.serviceTotal ?? selection.serviceTotal;
	const scheduled = calcFees(info.feeSchedule, serviceTotal);
	const deposit = booking?.deposit ?? scheduled.deposit;
	const supportFee = scheduled.supportFee;
	const amounts: BookingAmounts = useMemo(
		() => ({
			serviceTotal,
			deposit,
			supportFee,
			totalDue: deposit + supportFee,
			balance: Math.max(serviceTotal - deposit, 0),
		}),
		[serviceTotal, deposit, supportFee]
	);

	const selectedStylist = staffList.find(member => member.id === stylist) ?? null;
	const stylistLabel = selectedStylist ? selectedStylist.name : 'Any available stylist';
	const serviceNames = selection.selectedServices.map(s => s.name).join(', ');
	// Blank for services that aren't priced by either field, so a wash never gets
	// a size line it was never asked for. Summaries drop the row when it's empty.
	const sizeLengthLabel = [
		selection.size && formatSize(selection.size),
		selection.length && formatLength(selection.length),
	]
		.filter(Boolean)
		.join(' · ');
	// Quantities are only spelled out above one, so a single extra reads as the
	// plain name it did before rather than as "Scalp Massage × 1".
	const addOnNames =
		selection.selectedAddOns
			.map(a => (a.quantity > 1 ? `${a.name} × ${a.quantity}` : a.name))
			.join(', ') || 'None';
	const dateLabel = selectedDate ? format(selectedDate, 'EEEE, MMMM d, yyyy') : '';
	const timeLabel = selectedSlot ? selectedSlot.label || selectedSlot.time : '';
	const describedServices = selection.selectedServices.filter(s => s.description?.trim());

	// Stripe's card form no longer asks for name, email or phone — these are
	// sent as billing details instead, so the customer types them once.
	const billingDetails = useMemo(
		() => ({
			name: `${client.firstName} ${client.lastName}`.trim(),
			email: client.email.trim(),
			phone: client.phone.trim(),
		}),
		[client.firstName, client.lastName, client.email, client.phone]
	);

	// Built once at create time so it can be stored and replayed if Stripe sends
	// the customer off-site to authenticate. Takes the amounts explicitly: at
	// the call site the create response has not landed in state yet, so the
	// server's figures have to be passed in rather than read from `amounts`.
	const buildConfirmationRows = (a: BookingAmounts): ConfirmationRow[] => [
		{ label: 'Client', value: `${client.firstName} ${client.lastName}`.trim() },
		{ label: 'Phone', value: client.phone },
		{ label: 'Services', value: serviceNames },
		{ label: 'Add-Ons', value: addOnNames },
		...(sizeLengthLabel ? [{ label: 'Size / Length', value: sizeLengthLabel }] : []),
		{ label: 'Stylist', value: stylistLabel },
		{ label: 'Date', value: dateLabel },
		{ label: 'Time', value: timeLabel },
		{ label: 'Service Price', value: formatBookingCurrency(a.serviceTotal) },
		{ label: 'Deposit', value: formatBookingCurrency(a.deposit) },
		{ label: 'Support Fee', value: formatBookingCurrency(a.supportFee) },
		{
			label: 'Total Paid Today',
			value: formatBookingCurrency(a.totalDue),
			variant: 'positive',
		},
		{ label: 'Balance Due at Appt.', value: formatBookingCurrency(a.balance) },
	];

	/** Nothing is charged for a slot that does not exist yet, so no fee rows. */
	const buildWaitlistRows = (): ConfirmationRow[] => [
		{ label: 'Client', value: `${client.firstName} ${client.lastName}`.trim() },
		{ label: 'Phone', value: client.phone },
		{ label: 'Services', value: serviceNames },
		{ label: 'Add-Ons', value: addOnNames },
		...(sizeLengthLabel ? [{ label: 'Size / Length', value: sizeLengthLabel }] : []),
		{ label: 'Stylist', value: stylistLabel },
		{ label: 'Requested Date', value: dateLabel },
		{ label: 'Preferred Time', value: waitlist ? WAITLIST_LABELS[waitlist] : '' },
		{ label: 'Status', value: 'Waiting list' },
		{ label: 'Estimated Total', value: formatBookingCurrency(selection.serviceTotal) },
		{ label: 'Paid Today', value: formatBookingCurrency(0), variant: 'positive' },
	];

	// Return leg from an off-site payment step. If the intent already succeeded,
	// jump straight to the confirmation rather than showing a fresh form the
	// customer might pay through a second time.
	useEffect(() => {
		const returnedSecret = getReturnedClientSecret(window.location.search);
		if (!returnedSecret) return;

		let cancelled = false;
		const settle = async () => {
			const snapshot = readConfirmationSnapshot(token);
			try {
				const key = snapshot?.publishableKey || info.stripePublishableKey;
				const status = key ? await retrievePaymentStatus(key, returnedSecret) : null;
				if (cancelled) return;

				if (status === 'succeeded' || status === 'processing') {
					if (snapshot) {
						setRestoredRows(snapshot.rows);
						setRestoredReference(snapshot.reference);
					}
					setIsPaid(true);
					clearConfirmationSnapshot(token);
				}
			} finally {
				if (!cancelled) {
					stripPaymentParamsFromUrl();
					setIsCheckingReturn(false);
				}
			}
		};

		settle();
		return () => {
			cancelled = true;
		};
	}, [token, info.stripePublishableKey]);

	// Stable identities: BookingDateTime watches these in an effect, and a fresh
	// closure each render would make it re-run on every keystroke elsewhere.
	const handleSlotChange = useCallback((slot: PublicBookingSlot | null) => {
		setSelectedSlot(slot);
		// The two are mutually exclusive: a chosen time is not a waiting-list
		// request, and vice versa.
		if (slot) setWaitlist(null);
	}, []);

	const handleWaitlistChange = useCallback((preference: WaitlistPreference | null) => {
		setWaitlist(preference);
		if (preference) setSelectedSlot(null);
	}, []);

	/**
	 * Availability is read per stylist, so switching stylist invalidates both a
	 * chosen slot and a waiting-list request: a time that was free for one may
	 * be taken for another, and a day that was full for one may not be.
	 */
	const handleStylistChange = useCallback((value: number | null) => {
		setStylist(value);
		setSelectedSlot(null);
		setWaitlist(null);
	}, []);

	const updateClient = (field: keyof ClientForm, value: string) => {
		setClient(prev => ({ ...prev, [field]: value }));
		setFieldErrors(prev => ({ ...prev, [field]: undefined }));
	};

	const goToStep = (step: number, subStep: ServiceSubStep = 'service') => {
		setStepError(null);
		setServiceSubStep(subStep);
		setCurrentStep(step);
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	// ── Per-step validation ──
	const handleContinueFromStylist = () => {
		if (stylist === undefined) {
			setStepError('Please select a stylist, or choose "Any Available Stylist".');
			return;
		}
		goToStep(STEP_CLIENT);
	};

	const handleContinueFromClient = () => {
		const errors: Partial<Record<keyof ClientForm, string>> = {};
		if (!client.firstName.trim()) errors.firstName = 'First name is required.';
		if (!client.lastName.trim()) errors.lastName = 'Last name is required.';
		if (!client.phone.trim()) errors.phone = 'Phone number is required.';
		if (!client.email.trim()) errors.email = 'Email address is required.';
		else if (!EMAIL_PATTERN.test(client.email.trim())) errors.email = 'Enter a valid email.';
		setFieldErrors(errors);
		if (Object.keys(errors).length > 0) return;
		goToStep(STEP_SERVICE);
	};

	/** Service → Add-ons, the second half of step 3. */
	const handleContinueFromService = () => {
		if (selection.selectedServices.length === 0) {
			setStepError('Please select at least one service.');
			return;
		}
		// Only asked for when the chosen services are priced by them — and then
		// answered, so the total shown is the one the server will charge.
		if (selection.requiresSize && !selection.size) {
			setStepError('Please select a size.');
			return;
		}
		if (selection.requiresLength && !selection.length) {
			setStepError('Please select a length.');
			return;
		}
		goToStep(STEP_SERVICE, 'addons');
	};

	const handleSkipAddOns = () => {
		selection.clearAddOns();
		goToStep(STEP_DATETIME);
	};

	const handleContinueFromDateTime = () => {
		if (!selectedDate) {
			setStepError('Please select a date.');
			return;
		}
		if (!selectedSlot && !joiningWaitlist) {
			setStepError('Please select a time slot, or join the waiting list for this date.');
			return;
		}
		goToStep(STEP_REVIEW);
	};

	/**
	 * Creates the appointment, then either moves to payment or — for a waiting
	 * list request, which takes no deposit — straight to the confirmation.
	 *
	 * Guarded by a signature of everything that goes into the payload: stepping
	 * back to change a detail and returning creates a fresh booking, while
	 * simply going back and forth reuses the one already made rather than
	 * leaving a trail of duplicate appointments behind.
	 */
	const handleProceedFromReview = async () => {
		if (!selectedDate) return;
		if (!selectedSlot && !joiningWaitlist) return;

		const appointmentDateTime =
			selectedSlot && !joiningWaitlist
				? buildAppointmentDateTime(selectedDate, selectedSlot)
				: buildWaitlistDateTime(selectedDate);

		const signature = JSON.stringify({
			client,
			serviceIds: selection.selectedIds,
			addOnQuantities: selection.addOnQuantities,
			size: selection.size,
			length: selection.length,
			notes: notes.trim(),
			appointmentDateTime,
			stylist,
			waitlist,
		});

		if (booking && bookingSignature === signature) {
			if (joiningWaitlist) setIsWaitlisted(true);
			else goToStep(STEP_PAYMENT);
			return;
		}

		setIsCreating(true);
		setStepError(null);
		try {
			const response = await publicBookingApi.create(token, {
				firstName: client.firstName.trim(),
				lastName: client.lastName.trim(),
				phone: client.phone.trim(),
				email: client.email.trim(),
				serviceIds: selection.selectedIds,
				...(selection.size && { size: selection.size }),
				...(selection.length && { length: selection.length }),
				notes: notes.trim(),
				appointmentDateTime,
				...(stylist != null && { staffId: stylist }),
				// Only `addOnQuantities` is sent — never alongside `addOnIds`,
				// which describes the same basket and could be counted twice.
				...(selection.selectedAddOnIds.length > 0 && {
					addOnQuantities: selection.addOnQuantities,
				}),
				...(waitlist !== null && { waitlist: { preference: waitlist } }),
			});
			setBooking(response);
			setBookingSignature(signature);

			if (joiningWaitlist) {
				setIsWaitlisted(true);
				window.scrollTo({ top: 0, behavior: 'smooth' });
				return;
			}

			if (response.clientSecret) {
				const serverFees = calcFees(info.feeSchedule, response.serviceTotal);
				const serverDeposit = response.deposit ?? serverFees.deposit;
				saveConfirmationSnapshot(token, {
					reference: response.reference,
					clientSecret: response.clientSecret,
					publishableKey:
						response.stripePublishableKey || info.stripePublishableKey || '',
					rows: buildConfirmationRows({
						serviceTotal: response.serviceTotal,
						deposit: serverDeposit,
						supportFee: serverFees.supportFee,
						totalDue: serverDeposit + serverFees.supportFee,
						balance: Math.max(response.serviceTotal - serverDeposit, 0),
					}),
				});
			}
			goToStep(STEP_PAYMENT);
		} catch (error) {
			const detail =
				error instanceof ApiError
					? error.getFieldErrorMessages() || error.message
					: 'Please try again.';
			setStepError(`We could not start your booking. ${detail}`);
		} finally {
			setIsCreating(false);
		}
	};

	const handlePaid = () => {
		setIsPaid(true);
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	const publishableKey = booking?.stripePublishableKey || info.stripePublishableKey;

	// Coming back from an off-site payment step: hold the wizard until we know
	// whether the deposit went through.
	if (isCheckingReturn) {
		return (
			<div className="pb pb--paid">
				<div className="pb__state">
					<div className="pb__spinner" />
					<div className="pb__state-text">Confirming your payment…</div>
				</div>
			</div>
		);
	}

	const showStep = (step: number) => !isConfirmed && currentStep === step;

	return (
		<div className="pb pb--paid">
			{/* Topbar */}
			<div className="pb__topbar">
				<div className="pb__topbar-inner">
					<div className="pb__brand">
						<div className="pb__brand-logo">
							<svg
								width="22"
								height="22"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
								<polyline points="9 22 9 12 15 12 15 22" />
							</svg>
						</div>
						<div>
							<div className="pb__brand-name">{info.providerName}</div>
							{info.locationName && (
								<div className="pb__brand-tagline">{info.locationName}</div>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* Hero */}
			<div className="pb__hero">
				<div className="pb__hero-eyebrow">Schedule Your Visit</div>
				<div className="pb__hero-title">Book an Appointment</div>
				<div className="pb__hero-sub">
					Choose your pro, service, and preferred time. We can&apos;t wait to work with
					you!
				</div>
			</div>

			<div className="pb__content">
				<div className="pb__content-inner">
					{!isConfirmed && <BookingProgress steps={STEPS} currentStep={currentStep} />}

					{/* ── Step 1: Stylist ── */}
					{showStep(STEP_STYLIST) && (
						<div className="pb__card">
							<div className="pb__card-header">
								<div className="pb__card-title">Choose Your Pro</div>
								<div className="pb__card-desc">
									Select the stylist you&apos;d like to work with
								</div>
							</div>

							{stepError && <ErrorBanner message={stepError} />}

							<div className="pb__actions pb__actions--top">
								<div />
								<button
									type="button"
									className="pb__btn pb__btn--primary"
									onClick={handleContinueFromStylist}
								>
									Continue →
								</button>
							</div>

							<StylistPicker
								staff={staffList}
								value={stylist}
								onChange={handleStylistChange}
								onBookFromModal={() => goToStep(STEP_CLIENT)}
							/>

							<div className="pb__actions">
								<div />
								<button
									type="button"
									className="pb__btn pb__btn--primary"
									onClick={handleContinueFromStylist}
								>
									Continue →
								</button>
							</div>
						</div>
					)}

					{/* ── Step 2: Client Info ── */}
					{showStep(STEP_CLIENT) && (
						<div className="pb__card">
							<div className="pb__card-header">
								<div className="pb__card-title">Client Information</div>
								<div className="pb__card-desc">Enter your details</div>
							</div>

							<div className="pb__actions pb__actions--top">
								<button
									type="button"
									className="pb__btn pb__btn--secondary"
									onClick={() => goToStep(STEP_STYLIST)}
								>
									← Back
								</button>
								<button
									type="button"
									className="pb__btn pb__btn--primary"
									onClick={handleContinueFromClient}
								>
									Continue →
								</button>
							</div>

							<div className="pb__form-grid">
								<TextField
									label="First Name"
									required
									value={client.firstName}
									onChange={value => updateClient('firstName', value)}
									placeholder="e.g. Kenya"
									error={fieldErrors.firstName}
								/>
								<TextField
									label="Last Name"
									required
									value={client.lastName}
									onChange={value => updateClient('lastName', value)}
									placeholder="e.g. Bennett"
									error={fieldErrors.lastName}
								/>
								<TextField
									label="Phone Number"
									required
									type="tel"
									value={client.phone}
									onChange={value => updateClient('phone', value)}
									placeholder="(718) 555-0000"
									error={fieldErrors.phone}
								/>
								<TextField
									label="Email Address"
									required
									type="email"
									value={client.email}
									onChange={value => updateClient('email', value)}
									placeholder="client@email.com"
									error={fieldErrors.email}
								/>
							</div>

							<div className="pb__actions">
								<button
									type="button"
									className="pb__btn pb__btn--secondary"
									onClick={() => goToStep(STEP_STYLIST)}
								>
									← Back
								</button>
								<button
									type="button"
									className="pb__btn pb__btn--primary"
									onClick={handleContinueFromClient}
								>
									Continue →
								</button>
							</div>
						</div>
					)}

					{/* ── Step 3a: Service ── */}
					{showStep(STEP_SERVICE) && serviceSubStep === 'service' && (
						<div className="pb__card">
							<div className="pb__card-header">
								<div className="pb__card-title">Service Details</div>
								<div className="pb__card-desc">
									Choose your services, size and length
								</div>
							</div>

							<SubSteps active="service" />

							{stepError && <ErrorBanner message={stepError} />}

							<div className="pb__actions pb__actions--top">
								<button
									type="button"
									className="pb__btn pb__btn--secondary"
									onClick={() => goToStep(STEP_CLIENT)}
								>
									← Back
								</button>
								<button
									type="button"
									className="pb__btn pb__btn--primary"
									onClick={handleContinueFromService}
								>
									Continue →
								</button>
							</div>

							<div className="pb__section-label">
								Select Service
								<span className="pb__section-hint">(select all that apply)</span>
							</div>

							<ServiceMultiSelect
								services={info.services}
								selectedIds={selection.selectedIds}
								onToggle={selection.toggleService}
								onRemove={selection.removeService}
								categories={selection.categories}
								activeCategory={selection.activeCategory}
								onCategoryChange={selection.setActiveCategory}
							/>

							{/* Hidden outright for services priced by neither field. A row
											carries size and length together, so a basket that
											leaves no combination to pick loses both questions at
											once rather than offering one of them on its own. */}
							{selection.groupClashes ? (
								<>
									<div className="pb__divider" />
									<div className="pb__section-label">Size &amp; Length</div>
									<div className="pb__empty-hint">
										The selected services have no size and length combination in
										common — your stylist will confirm both with you.
									</div>
								</>
							) : (
								<>
									{selection.sizeApplies && (
										<>
											<div className="pb__divider" />
											<div className="pb__section-label">Select Size</div>
											<div className="pb__pill-group">
												{selection.sizeOptions.map(option => (
													<button
														key={option}
														type="button"
														className={`pb__pill${selection.size === option ? ' is-selected' : ''}`}
														onClick={() => selection.setSize(option)}
													>
														{formatSize(option)}
													</button>
												))}
											</div>
										</>
									)}

									{selection.lengthApplies && (
										<>
											<div className="pb__divider" />
											<div className="pb__section-label">Select Length</div>
											<div className="pb__pill-group">
												{selection.lengthOptions.map(option => (
													<button
														key={option}
														type="button"
														className={`pb__pill${selection.length === option ? ' is-selected' : ''}`}
														onClick={() => selection.setLength(option)}
													>
														{formatLength(option)}
													</button>
												))}
											</div>
										</>
									)}
								</>
							)}

							{describedServices.length > 0 && (
								<>
									<div className="pb__divider" />
									<div className="pb__section-label">Service Description</div>
									<div className="pb-svcdesc">
										{describedServices.map(service => (
											<div key={service.id} className="pb-svcdesc__card">
												<div className="pb-svcdesc__name">
													{service.name}
												</div>
												<div className="pb-svcdesc__text">
													{service.description}
												</div>
											</div>
										))}
									</div>
								</>
							)}

							<div className="pb__divider" />
							<div>
								<label className="pb__label" htmlFor="paid-notes">
									Additional Notes
								</label>
								<textarea
									id="paid-notes"
									className="pb__textarea"
									value={notes}
									onChange={event => setNotes(event.target.value)}
									placeholder="Enter any special notes or requirements"
								/>
							</div>

							<div className="pb__actions">
								<button
									type="button"
									className="pb__btn pb__btn--secondary"
									onClick={() => goToStep(STEP_CLIENT)}
								>
									← Back
								</button>
								<button
									type="button"
									className="pb__btn pb__btn--primary"
									onClick={handleContinueFromService}
								>
									Continue →
								</button>
							</div>
						</div>
					)}

					{/* ── Step 3b: Add-ons ── */}
					{showStep(STEP_SERVICE) && serviceSubStep === 'addons' && (
						<div className="pb__card">
							<div className="pb__card-header">
								<div className="pb__card-title">Add Extras</div>
								<div className="pb__card-desc">
									Optional — pick any extras, or skip straight to booking
								</div>
							</div>

							<SubSteps active="addons" />

							<div className="pb__actions pb__actions--top pb__actions--triple">
								<button
									type="button"
									className="pb__btn pb__btn--secondary"
									onClick={() => goToStep(STEP_SERVICE, 'service')}
								>
									← Back
								</button>
								<button
									type="button"
									className="pb__btn pb__btn--secondary"
									onClick={handleSkipAddOns}
								>
									Skip add-ons
								</button>
								<button
									type="button"
									className="pb__btn pb__btn--primary"
									onClick={() => goToStep(STEP_DATETIME)}
								>
									Continue →
								</button>
							</div>

							<AddOnPicker
								addOns={addOns}
								selected={selection.selectedAddOns}
								onToggle={selection.toggleAddOn}
								onQuantityChange={selection.setAddOnQuantity}
								serviceOnlyTotal={selection.serviceOnlyTotal}
								total={selection.serviceTotal}
							/>

							<div className="pb__actions pb__actions--triple">
								<button
									type="button"
									className="pb__btn pb__btn--secondary"
									onClick={() => goToStep(STEP_SERVICE, 'service')}
								>
									← Back
								</button>
								<button
									type="button"
									className="pb__btn pb__btn--secondary"
									onClick={handleSkipAddOns}
								>
									Skip add-ons
								</button>
								<button
									type="button"
									className="pb__btn pb__btn--primary"
									onClick={() => goToStep(STEP_DATETIME)}
								>
									Continue →
								</button>
							</div>
						</div>
					)}

					{/* ── Step 4: Date & Time ── */}
					{showStep(STEP_DATETIME) && (
						<div className="pb__card">
							<div className="pb__card-header">
								<div className="pb__card-title">Date &amp; Time</div>
								<div className="pb__card-desc">
									Choose your preferred appointment date and time slot
								</div>
							</div>

							{stepError && <ErrorBanner message={stepError} />}

							<div className="pb__actions pb__actions--top">
								<button
									type="button"
									className="pb__btn pb__btn--secondary"
									onClick={() => goToStep(STEP_SERVICE, 'addons')}
								>
									← Back
								</button>
								<button
									type="button"
									className="pb__btn pb__btn--primary"
									onClick={handleContinueFromDateTime}
								>
									Review Booking →
								</button>
							</div>

							<BookingDateTime
								token={token}
								staffId={stylist ?? undefined}
								durationMinutes={selection.totalDurationMinutes}
								selectedDate={selectedDate}
								selectedSlot={selectedSlot}
								onDateChange={setSelectedDate}
								onSlotChange={handleSlotChange}
								waitlistEnabled
								waitlist={waitlist}
								onWaitlistChange={handleWaitlistChange}
							/>

							<div className="pb__actions">
								<button
									type="button"
									className="pb__btn pb__btn--secondary"
									onClick={() => goToStep(STEP_SERVICE, 'addons')}
								>
									← Back
								</button>
								<button
									type="button"
									className="pb__btn pb__btn--primary"
									onClick={handleContinueFromDateTime}
								>
									Review Booking →
								</button>
							</div>
						</div>
					)}

					{/* ── Step 5: Review ── */}
					{showStep(STEP_REVIEW) && (
						<div className="pb__card">
							<div className="pb__card-header">
								<div className="pb__card-title">Review &amp; Confirm</div>
								<div className="pb__card-desc">
									{joiningWaitlist
										? 'Check your details before sending your request'
										: 'Check your details before proceeding to payment'}
								</div>
							</div>

							{stepError && <ErrorBanner message={stepError} />}

							<ReviewActions
								onBack={() => goToStep(STEP_DATETIME)}
								onProceed={handleProceedFromReview}
								isCreating={isCreating}
								joiningWaitlist={joiningWaitlist}
								position="top"
							/>

							<div className="pb__section-label">Client</div>
							<div className="pb__summary">
								<BookingSummaryRow
									label="Name"
									value={`${client.firstName} ${client.lastName}`}
								/>
								<BookingSummaryRow label="Phone" value={client.phone} />
								<BookingSummaryRow label="Email" value={client.email} />
							</div>

							<div className="pb__divider" />
							<div className="pb__section-label">Service</div>
							<div className="pb__summary">
								<BookingSummaryRow label="Services" value={serviceNames} />
								<BookingSummaryRow label="Add-Ons" value={addOnNames} />
								{selection.size && (
									<BookingSummaryRow
										label="Size"
										value={formatSize(selection.size)}
									/>
								)}
								{selection.length && (
									<BookingSummaryRow
										label="Length"
										value={formatLength(selection.length)}
									/>
								)}
								<BookingSummaryRow label="Stylist" value={stylistLabel} />
								{notes.trim() && (
									<BookingSummaryRow
										label="Notes"
										value={notes.trim()}
										variant="normal"
									/>
								)}
							</div>

							<div className="pb__divider" />
							<div className="pb__section-label">Appointment</div>
							<div className="pb__summary">
								<BookingSummaryRow label="Date" value={dateLabel} />
								{waitlist !== null ? (
									<>
										<BookingSummaryRow label="Status" value="Waiting list" />
										<BookingSummaryRow
											label="Preferred Time"
											value={WAITLIST_LABELS[waitlist]}
										/>
									</>
								) : (
									<BookingSummaryRow label="Time" value={timeLabel} />
								)}
							</div>

							{joiningWaitlist ? (
								<div className="pb-wl__review">
									<div className="pb-wl__review-head">
										<span>📋</span>
										<span>Waiting list request</span>
									</div>
									<div className="pb-wl__review-text">
										No payment is taken now. We&apos;ll contact you if a spot
										opens on this date, and you&apos;ll pay the deposit only
										once you confirm a time. Estimated service total is{' '}
										<strong>
											{formatBookingCurrency(selection.serviceTotal)}
										</strong>
										.
									</div>
								</div>
							) : (
								<div className="pb-deposit">
									<div className="pb-deposit__row pb-deposit__row--top">
										<div className="pb-deposit__label">Service Total</div>
										<div className="pb-deposit__value">
											{formatBookingCurrency(amounts.serviceTotal)}
										</div>
									</div>
									<div className="pb-deposit__row">
										<div className="pb-deposit__total-label">
											Due Today (Deposit + Fee)
										</div>
										<div className="pb-deposit__total-value">
											{formatBookingCurrency(amounts.totalDue)}
										</div>
									</div>
								</div>
							)}

							<ReviewActions
								onBack={() => goToStep(STEP_DATETIME)}
								onProceed={handleProceedFromReview}
								isCreating={isCreating}
								joiningWaitlist={joiningWaitlist}
								position="bottom"
							/>
						</div>
					)}

					{/* ── Step 6: Payment ── */}
					{showStep(STEP_PAYMENT) && (
						<div className="pb__card">
							<div className="pb__card-header">
								<div className="pb__card-title">Payment</div>
								<div className="pb__card-desc">
									Pay your deposit to confirm the appointment
								</div>
							</div>

							{booking?.clientSecret && publishableKey ? (
								<BookingPayment
									publishableKey={publishableKey}
									clientSecret={booking.clientSecret}
									amounts={amounts}
									billingDetails={billingDetails}
									onBack={() => goToStep(STEP_REVIEW)}
									onPaid={handlePaid}
								/>
							) : (
								<>
									<ErrorBanner message="This booking link is not set up to take payments yet. Please contact the salon to confirm your appointment." />
									<div className="pb__actions">
										<button
											type="button"
											className="pb__btn pb__btn--secondary"
											onClick={() => goToStep(STEP_REVIEW)}
										>
											← Back
										</button>
									</div>
								</>
							)}
						</div>
					)}

					{/* ── Confirmed ── */}
					{isConfirmed && (
						<div className="pb__card">
							<div className="pb__confirm-hero">
								<div className="pb__confirm-icon">{isWaitlisted ? '📋' : '✓'}</div>
								<div className="pb__confirm-title">
									{isWaitlisted
										? "You're on the waiting list"
										: 'Booking Confirmed!'}
								</div>
								<div className="pb__confirm-sub">
									{isWaitlisted
										? `No payment taken. We'll contact you at ${client.phone || client.email} if a spot opens.`
										: 'Your deposit has been received. See you soon!'}
								</div>
								{(restoredReference || booking?.reference) && (
									<div className="pb__confirm-ref">
										Booking #{restoredReference || booking?.reference}
									</div>
								)}
							</div>

							<div className="pb__divider" />
							<div className="pb__section-label">
								{isWaitlisted ? 'Waiting List Request' : 'Booking Summary'}
							</div>
							<div className="pb__summary">
								{(isWaitlisted
									? buildWaitlistRows()
									: (restoredRows ?? buildConfirmationRows(amounts))
								).map(row => (
									<BookingSummaryRow
										key={row.label}
										label={row.label}
										value={row.value}
										variant={row.variant}
									/>
								))}
							</div>

							<div className="pb__actions pb__actions--center">
								<button
									type="button"
									className="pb__btn pb__btn--secondary"
									onClick={() => window.location.reload()}
								>
									+ New Booking
								</button>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

// ── Local presentational helpers ────────────────────────────────

/** The Service → Add-ons breadcrumb shown inside step 3. */
const SubSteps = ({ active }: { active: ServiceSubStep }) => (
	<div className="pb__substeps">
		<span className={`pb__substep${active === 'service' ? ' is-active' : ' is-done'}`}>
			1. Service
		</span>
		<span className="pb__substep-sep">›</span>
		<span className={`pb__substep${active === 'addons' ? ' is-active' : ''}`}>2. Add-ons</span>
	</div>
);

/**
 * The review step's buttons, which change meaning with the path taken: a
 * waiting-list request is submitted here and never reaches step 6.
 */
const ReviewActions = ({
	onBack,
	onProceed,
	isCreating,
	joiningWaitlist,
	position,
}: {
	onBack: () => void;
	onProceed: () => void;
	isCreating: boolean;
	joiningWaitlist: boolean;
	position: 'top' | 'bottom';
}) => (
	<div className={`pb__actions${position === 'top' ? ' pb__actions--top' : ''}`}>
		<button
			type="button"
			className="pb__btn pb__btn--secondary"
			onClick={onBack}
			disabled={isCreating}
		>
			← Back
		</button>
		<button
			type="button"
			className="pb__btn pb__btn--primary"
			onClick={onProceed}
			disabled={isCreating}
		>
			{isCreating
				? joiningWaitlist
					? 'Sending…'
					: 'Preparing…'
				: joiningWaitlist
					? 'Join Waiting List →'
					: 'Proceed to Payment →'}
		</button>
	</div>
);

const TextField = ({
	label,
	value,
	onChange,
	placeholder,
	error,
	required,
	type = 'text',
}: {
	label: string;
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	error?: string;
	required?: boolean;
	type?: string;
}) => {
	const id = `paid-${label.toLowerCase().replace(/\s+/g, '-')}`;
	return (
		<div>
			<label className="pb__label" htmlFor={id}>
				{label}
				{required && <span className="pb__req">*</span>}
			</label>
			<input
				id={id}
				className={`pb__input${error ? ' has-error' : ''}`}
				type={type}
				value={value}
				onChange={event => onChange(event.target.value)}
				placeholder={placeholder}
			/>
			{error && <span className="pb__field-error">{error}</span>}
		</div>
	);
};

const ErrorBanner = ({ message }: { message: string }) => (
	<div className="pb__error-banner">
		<span>⚠️</span>
		<span>{message}</span>
	</div>
);

export default BookWithPayment;
